import React from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import { CalendarToday, PersonOutline, Business } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface SuggestedTimesDisplayProps {
  suggestedTimes: string[]; // Array of UTC datetime strings
  suggestedBy: 'decision' | 'reader'; // Who suggested these times
  suggestedByName?: string;
  onSelectTime?: (time: string) => void;
  onSuggestAlternative?: () => void;
  mode?: 'view' | 'select' | 'display';
  selectedTime?: string; // Currently selected time to highlight
  customTitle?: string; // Override the default title
}

export default function SuggestedTimesDisplay({ 
  suggestedTimes, 
  suggestedBy, 
  suggestedByName,
  onSelectTime,
  onSuggestAlternative,
  mode = 'view',
  selectedTime,
  customTitle
}: SuggestedTimesDisplayProps) {
  const { t, i18n } = useTranslation();

  const formatDisplayDateTime = (dateString: string) => {
    const date = new Date(dateString);
    
    // Get full weekday name in user's locale and capitalize first letter
    const weekdayName = date.toLocaleDateString(i18n.language, { weekday: 'long' });
    const capitalizedWeekday = weekdayName.charAt(0).toUpperCase() + weekdayName.slice(1);
    
    // Format date as DD/MM
    const day = date.getDate();
    const month = date.getMonth() + 1;
    
    // Format time in user's locale (24h format)
    const time = date.toLocaleTimeString(i18n.language, { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
    
    return { 
      dateStr: `${capitalizedWeekday} ${day}/${month}`,
      timeStr: time
    };
  };

  if (!suggestedTimes || suggestedTimes.length === 0) {
    return null;
  }

  const icon = <PersonOutline />;
  const byName = suggestedByName; // Always expect a name to be provided

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body1" sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem', fontWeight: 'bold' }}>
        <CalendarToday sx={{ fontSize: '1.1rem' }} />
        {!customTitle && icon}
        {customTitle || (mode === 'select' 
          ? t('suggested_times.select_title', `${byName} föreslog dessa tider`) 
          : t('suggested_times.view_title', `${byName} föreslog dessa tider`)
        )}
      </Typography>

      {mode === 'select' && (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, fontSize: '0.9rem' }}>
          {t('suggested_times.select_subtitle', 'Välj en av tiderna:')}
        </Typography>
      )}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {suggestedTimes.map((time, index) => {
          const { dateStr, timeStr } = formatDisplayDateTime(time);
          const isClickable = mode === 'select' && onSelectTime;
          const isSelected = selectedTime === time;
          
          return (
            <Box
              key={index}
              onClick={isClickable ? () => onSelectTime(time) : undefined}
              sx={{
                p: 1.5,
                backgroundColor: isSelected ? '#8E603C' : 'rgba(255,255,255,0.1)',
                borderRadius: 2,
                border: isSelected ? '2px solid #8E603C' : '2px solid transparent',
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isClickable 
                    ? (isSelected ? '#76451F' : 'rgba(255,255,255,0.15)')
                    : (isSelected ? '#8E603C' : 'rgba(255,255,255,0.1)'),
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    color: isSelected ? 'white' : 'white', 
                    fontWeight: 'bold', 
                    fontSize: '0.95rem' 
                  }}>
                    {dateStr}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: isSelected ? 'white' : 'white', 
                    fontSize: '1.1rem', 
                    fontWeight: 'bold' 
                  }}>
                    {timeStr}
                  </Typography>
                </Box>
                {isClickable && !isSelected && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                    {t('common.tap_to_select', 'Tryck för att välja')}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {mode === 'select' && onSuggestAlternative && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={onSuggestAlternative}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.8)',
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            {t('suggested_times.suggest_alternative', 'Föreslå andra tider istället')}
          </Button>
        </Box>
      )}
    </Box>
  );
}